import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SButton, SFormControl } from "../../components/BasicComponents";
import Chat, { MessageReply } from "../../components/Chat/Chat";
import Notes from "../../components/Notes/Notes";
import Participants from "../../components/Participants/Participants";
import { SocketContext } from "../../context/SocketContext";
import { TeacherContext } from "../../context/TeacherContext";
import { StudentContext } from "../../context/StudentContext";
import { MediaContext } from "../../context/MediaContext";
import shortLogo from "../../assets/images/evoliShortLogo.png";
import { isLectureLive, isLectureAvailable } from "../../utils/lectureUtils";
import Switch from "react-switch";
import Dashboard from "../../components/Dashboard/Dashboard";
import PDFReader from "../../components/PDFReader/PDFReader";
import { Styles, SidePanel, Sidenav } from "./Lecture.styles";
import "../../styles/reset.css";
import WaitingRoom from "../../components/WaitingRoom/WaitingRoom";
import { toastError } from "../../utils/Toast";
import Loading from "../../components/Loading/Loading";
import LectureVideoPlayer from "../../components/YouTubePlayer/LectureVideoPlayer";
import Questions from "../../components/Questions/Questions";
import FeedbackBar from "../../components/FeedbackBar/FeedbackBar";

const TeacherLecturePage = (props) => {
  const { t } = useTranslation("", { keyPrefix: "lecture.video_page" });

  const { connectSocket, disconnectSocket, setupGraphUpdate, stopGraphUpdate, functionalities } =
    useContext(SocketContext);

  const { selectLecture, currentLecture, getSlidesURL, loading } = useContext(TeacherContext);
  const { downloadSlides } = useContext(StudentContext);

  const history = useHistory();

  const [panel, enablePanel] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [slideURL, setSlideURL] = useState("");
  const [selectedTab, setSelectedTab] = useState("chat");
  const [showSecondaryPanel, setShowSecondaryPanel] = useState(true);
  const [videoMainContent, setVideoMainContent] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [stopped, setStopped] = useState(false);
  const [duration, setDuration] = useState(0);
  const youtubeVisible = videoMainContent || (!videoMainContent && showSecondaryPanel);

  useEffect(() => {
    //If the URL is correctly formatted
    if (props.match.path === "/teacher/lecture/:lectureUUID") {
      const lectureUUID = props.match.params.lectureUUID;
      selectLecture(lectureUUID).then((lecture) => {
        if (lecture) {
          setSlideURL(getSlidesURL(lectureUUID));
          connectSocket(true, lectureUUID);
          setupGraphUpdate();
        } else {
          toastError(t("toast.lecture_error"));
          history.push("/teacher");
        }
      });

      return () => {
        disconnectSocket();
        stopGraphUpdate();
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickMenuItem = (tabName) => {
    if (tabName === selectedTab) enablePanel(!panel);
    else enablePanel(true);
    setSelectedTab(tabName);
    setShowFeedback(false);
  };

  const onClickFeedback = () => {
    setShowFeedback(!showFeedback);
    if (window.innerWidth < 768) enablePanel(false);
  };

  const isSidePanelVisible = () => {
    if (!panel) return false;
    if (!functionalities.chat && selectedTab === "chat") return false;
    if (!functionalities.notes && selectedTab === "notes") return false;
    if (!functionalities.feedback && selectedTab === "feedback") return false;
    return true;
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          {isLectureAvailable(currentLecture) ? (
            <Styles
              secondaryContent={currentLecture.slides}
              panelVisible={panel}
              bottomPanelVisible={showFeedback}
              youtubeVisible={youtubeVisible}
            >
              <LectureVideoPlayer
                videoId={currentLecture.videoId}
                mainContent={videoMainContent}
                setStopped={setStopped}
                setDuration={setDuration}
              />

              {!isLectureLive(currentLecture) && stopped && (
                <FeedbackBar isSidePanelVisible={isSidePanelVisible()} duration={duration} />
              )}

              {currentLecture.slides && (!videoMainContent || (videoMainContent && showSecondaryPanel)) && (
                <div className={videoMainContent ? "secondary-content" : "main-content"}>
                  <PDFReader
                    filePath={slideURL}
                    isTeacher={true}
                    expanded={!videoMainContent}
                    setExpanded={setVideoMainContent}
                    downloadSlides={() => downloadSlides(currentLecture.uuid)}
                  />
                </div>
              )}

              <SidePanel visible={panel} selected={selectedTab}>
                <Row className="flex-grow-1 content-row">
                  {selectedTab === "chat" && <Chat replyClick={(messageUUID) => setReplyTo(messageUUID)} />}
                  {selectedTab === "notes" && functionalities.notes && <Notes />}
                  {selectedTab === "participants" && <Participants isTeacher={true} />}
                  {selectedTab === "settings" && <Settings />}
                  {selectedTab === "questions" && <Questions replyClick={(messageUUID) => setReplyTo(messageUUID)} />}
                </Row>
                <Row>
                  {(selectedTab === "chat" || (selectedTab === "questions" && replyTo)) && (
                    <SendForm replyingTo={replyTo} removeReply={() => setReplyTo(null)} />
                  )}
                </Row>
              </SidePanel>

              <Dashboard lecture={currentLecture} panelVisible={panel} visible={showFeedback} />

              <Sidenav expand="md">
                <div className="menu">
                  <div className="logo" onClick={() => history.push("/")}>
                    <img src={shortLogo} alt="EVOLI" />
                    <p className="p-0 m-0 fs-5 lecture-type">{isLectureLive(currentLecture) ? "● LIVE" : "REC"}</p>
                  </div>
                  {functionalities.chat && isLectureLive(currentLecture) && (
                    <button
                      className={classNames("menu-item", { active: selectedTab === "chat" && panel })}
                      onClick={() => onClickMenuItem("chat")}
                    >
                      <i className="fas fa-comment-dots w-auto" />
                      <span>{t("chat")}</span>
                    </button>
                  )}
                  {!isLectureLive(currentLecture) && (
                    <button
                      className={classNames("menu-item", { active: selectedTab === "questions" && panel })}
                      onClick={() => onClickMenuItem("questions")}
                    >
                      <i className="fas fa-question w-auto" />
                      <span>Questions</span>
                    </button>
                  )}
                  {functionalities.notes && (
                    <button
                      className={classNames("menu-item", { active: selectedTab === "notes" && panel })}
                      onClick={() => onClickMenuItem("notes")}
                    >
                      <i className="fas fa-book w-auto" />
                      <span>{t("notes")}</span>
                    </button>
                  )}
                  <button
                    className={classNames("menu-item", { active: selectedTab === "participants" && panel })}
                    onClick={() => onClickMenuItem("participants")}
                  >
                    <i className="fas fa-users w-auto" />
                    <span>{t("participants")}</span>
                  </button>
                  <button
                    className={classNames("menu-item", { active: showFeedback })}
                    onClick={() => onClickFeedback()}
                  >
                    <i className="fas fa-chart-bar w-auto" />
                    <span>{t("feedback")}</span>
                  </button>
                  {!videoMainContent && (
                    <button
                      className={classNames("menu-item", { active: showSecondaryPanel })}
                      onClick={() => setShowSecondaryPanel(!showSecondaryPanel)}
                    >
                      <i className="fas fa-video w-auto" />
                      <span>{t("video")}</span>
                    </button>
                  )}
                  {videoMainContent && currentLecture.slides && (
                    <button
                      className={classNames("menu-item slides", { active: showSecondaryPanel })}
                      onClick={() => setShowSecondaryPanel(!showSecondaryPanel)}
                    >
                      <i className="fas fa-file-pdf w-auto" />
                      <span>{t("slides")}</span>
                    </button>
                  )}
                  <button
                    className={classNames("menu-item", { active: selectedTab === "settings" && panel })}
                    onClick={() => onClickMenuItem("settings")}
                  >
                    <i className="fas fa-cog w-auto" />
                    <span>{t("settings")}</span>
                  </button>
                </div>
              </Sidenav>
            </Styles>
          ) : (
            <WaitingRoom lecture={currentLecture} />
          )}
        </>
      )}
    </>
  );
};

const SendForm = ({ replyingTo = null, removeReply }) => {
  const { t } = useTranslation("", { keyPrefix: "components.chat" });
  const [message, setMessage] = useState("");

  const { sendSocketEvent, chatMessages } = useContext(SocketContext);
  const { getVideoTs, slideNumber } = useContext(MediaContext);

  const onSend = (e) => {
    e.preventDefault();

    if (message.length > 0) {
      sendSocketEvent("chat/message", {
        text: message,
        videoTs: getVideoTs(),
        slideNumber: slideNumber,
        parent: replyingTo || undefined,
      });

      const form = document.getElementById("form-chat");
      form.reset();

      removeReply();
    }
  };

  return (
    <Col>
      {replyingTo && (
        <div>
          <button className="fas fa-times position-absolute" onClick={removeReply} />
          <MessageReply message={chatMessages[replyingTo]} />
        </div>
      )}

      <Form id="form-chat" className="m-2" onSubmit={onSend}>
        <Row>
          <InputGroup>
            <SFormControl
              className="p-3 fs-5"
              type="text"
              placeholder={t("send")}
              onChange={(e) => setMessage(e.target.value)}
            />
            <Button type="submit" className="send-button">
              <i className="fas fs-4 p-3 pe-4 fa-paper-plane" />
            </Button>
          </InputGroup>
        </Row>
      </Form>
    </Col>
  );
};

const Settings = () => {
  const { t } = useTranslation("", { keyPrefix: "lecture.video_page" });
  const { sendSocketEvent, functionalities } = useContext(SocketContext);
  const { currentLecture } = useContext(TeacherContext);

  const onChange = ({
    chatActive = functionalities.chat,
    notesActive = functionalities.notes,
    feedbackActive = functionalities.feedback,
  }) => {
    sendSocketEvent("teacher/functionalities", { chat: chatActive, notes: notesActive, feedback: feedbackActive });
  };

  const endLecture = () => {
    sendSocketEvent("teacher/endLecture");
  };

  return (
    <div className="d-flex flex-column justify-content-between">
      <div className="d-flex flex-column">
        <h3 className="text-center">
          <i className="fas fa-cog mx-1"></i>
          {t("settings")}
        </h3>
        {isLectureLive(currentLecture) && (
          <div className="d-flex flex-row justify-content-between mb-3">
            <span>{t("settings_options.chat")}</span>
            <Switch onChange={(chatActive) => onChange({ chatActive })} checked={functionalities.chat} />
          </div>
        )}
        <div className="d-flex flex-row justify-content-between mb-3">
          <span>{t("settings_options.notes")}</span>
          <Switch onChange={(notesActive) => onChange({ notesActive })} checked={functionalities.notes} />
        </div>
        <div className="d-flex flex-row justify-content-between mb-auto">
          <span>{t("settings_options.feedback")} </span>
          <Switch onChange={(feedbackActive) => onChange({ feedbackActive })} checked={functionalities.feedback} />
        </div>
      </div>
      <div>
        {isLectureLive(currentLecture) && (
          <SButton className="w-100" onClick={endLecture}>
            {t("end_lecture")}
          </SButton>
        )}
      </div>
    </div>
  );
};

export default TeacherLecturePage;
